import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import "./AddPerson.css";

export default function AddPerson() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name:"", department:"", email:"", skills:"", summary:"" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");

  if (!user || user.role !== "admin") {
    return (
      <div className="access-denied">
        <span className="access-icon">🔒</span>
        <p>Admin access required to add people to the directory.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`/api/people`, {
        method: "POST",
        headers: {
          "Content-Type":"application/json"
        },
        credentials: 'include', // Include cookies for better-auth session
        body: JSON.stringify({
          ...form,
          skills: form.skills ? form.skills.split(",").map(s=>s.trim()) : []
        })
      });
      const data = await res.json();
      
      if (res.ok) {
        setMessage({
          type: "success",
          text: `✅ ${form.name} has been added to the directory!`
        });
        setForm({ name:"", department:"", email:"", skills:"", summary:"" });
      } else {
        setMessage({
          type: "error", 
          text: data.error || "Failed to add person"
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: "Something went wrong. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkData.trim()) {
      setBulkMessage({ type: "error", text: "Please enter some data to import" });
      return;
    }

    setBulkImporting(true);
    setBulkMessage("");

    try {
      // Parse the bulk data - expecting JSON format
      let people;
      try {
        people = JSON.parse(bulkData);
      } catch (parseError) {
        setBulkMessage({ type: "error", text: "Invalid JSON format. Please check your data." });
        setBulkImporting(false);
        return;
      }

      if (!Array.isArray(people)) {
        setBulkMessage({ type: "error", text: "Data should be an array of people objects" });
        setBulkImporting(false);
        return;
      }

      const response = await fetch(`/api/bulk-import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify({ people })
      });

      const result = await response.json();

      if (response.ok) {
        setBulkMessage({ 
          type: "success", 
          text: `✅ Bulk import completed! Imported: ${result.summary.imported}, Skipped: ${result.summary.skipped}, Errors: ${result.summary.errors}` 
        });
        setBulkData(""); // Clear the textarea
      } else {
        setBulkMessage({ type: "error", text: result.error || "Bulk import failed" });
      }

    } catch (error) {
      setBulkMessage({ type: "error", text: "Network error during bulk import" });
    } finally {
      setBulkImporting(false);
    }
  };

  const loadImperialData = () => {
    const imperialStaff = [
      { name: "Peter Allison", title: "Prof.", link: "http://www.imperial.ac.uk/people/p.a.allison", department: "Earth Science & Engineering" },
      { name: "Rossella Arcucci", title: "Dr", link: "https://www.imperial.ac.uk/people/r.arcucci", department: "Earth Science & Engineering" },
      { name: "Ian Bastow", title: "Dr", link: "http://www.imperial.ac.uk/people/i.bastow", department: "Earth Science & Engineering" },
      { name: "Rebecca E Bell", title: "Dr", link: "https://www.imperial.ac.uk/people/rebecca.bell", department: "Earth Science & Engineering" },
      { name: "Martin Blunt", title: "Prof.", link: "http://www.imperial.ac.uk/people/m.blunt", department: "Earth Science & Engineering" }
      // Add more as needed...
    ];

    setBulkData(JSON.stringify(imperialStaff, null, 2));
  };

  const handleInputChange = (field, value) => {
    setForm({...form, [field]: value});
    // Clear message when user starts typing
    if (message) setMessage("");
  };

  return (
    <div className="add-person-container">
      <div className="add-person-header">
        <h3>Add New Person</h3>
        <p>Add someone to the people directory</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="add-person-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input 
              id="name"
              name="name" 
              value={form.name} 
              onChange={e=>handleInputChange('name', e.target.value)} 
              placeholder="Enter full name" 
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input 
              id="email"
              name="email" 
              type="email" 
              value={form.email} 
              onChange={e=>handleInputChange('email', e.target.value)} 
              placeholder="Enter email address"
              className="form-input"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="department">Department</label>
            <input 
              id="department"
              name="department" 
              value={form.department} 
              onChange={e=>handleInputChange('department', e.target.value)} 
              placeholder="Enter department or affiliation"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="skills">Skills</label>
            <input 
              id="skills"
              name="skills" 
              value={form.skills} 
              onChange={e=>handleInputChange('skills', e.target.value)} 
              placeholder="Enter skills (comma separated)"
              className="form-input"
            />
            <small className="form-hint">Separate multiple skills with commas</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="summary">Summary</label>
          <textarea 
            id="summary"
            name="summary" 
            value={form.summary} 
            onChange={e=>handleInputChange('summary', e.target.value)} 
            placeholder="Enter a brief summary or description"
            className="form-textarea"
            rows="4"
          />
        </div>

        <button 
          type="submit" 
          disabled={loading || !form.name.trim()}
          className={`submit-button ${loading ? 'loading' : ''}`}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Adding Person...
            </>
          ) : (
            <>
              <span className="button-icon">➕</span>
              Add to Directory
            </>
          )}
        </button>
      </form>

      {/* Bulk Import Section */}
      <div className="bulk-import-section" style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '30px' }}>
        <h3 style={{ color: '#2d3748', marginBottom: '20px' }}>
          📊 Bulk Import Data
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={loadImperialData}
            className="load-sample-btn"
            style={{ 
              backgroundColor: '#4299e1', 
              color: 'white', 
              border: 'none', 
              padding: '8px 16px', 
              borderRadius: '6px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            🏛️ Load Imperial College Data
          </button>
          <small style={{ color: '#718096' }}>
            Click to load sample Imperial College staff data in JSON format
          </small>
        </div>

        {bulkMessage && (
          <div className={`message ${bulkMessage.type}`} style={{ marginBottom: '15px' }}>
            {bulkMessage.text}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="bulkData">JSON Data to Import</label>
          <textarea
            id="bulkData"
            value={bulkData}
            onChange={(e) => setBulkData(e.target.value)}
            placeholder={`Enter JSON array of people objects, e.g.:
[
  {
    "name": "John Doe",
    "title": "Prof.",
    "department": "Computer Science", 
    "email": "j.doe@imperial.ac.uk",
    "link": "https://profiles.imperial.ac.uk/john.doe"
  }
]`}
            className="form-input"
            rows={10}
            style={{ fontFamily: 'monospace', fontSize: '14px' }}
          />
          <small className="form-hint">
            Expected format: JSON array with objects containing name, title, department, email, and link fields
          </small>
        </div>

        <button
          onClick={handleBulkImport}
          disabled={bulkImporting || !bulkData.trim()}
          className={`submit-button ${bulkImporting ? 'loading' : ''}`}
          style={{ backgroundColor: '#48bb78' }}
        >
          {bulkImporting ? (
            <>
              <span className="spinner"></span>
              Importing Data...
            </>
          ) : (
            <>
              <span className="button-icon">📥</span>
              Import Data
            </>
          )}
        </button>
      </div>
    </div>
  );
}
